/**
 * Support Tickets Widget
 *
 * Shows open/in-progress support requests on the dashboard.
 * Compact view with type boxes (count + filter) and priority filter.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LifeBuoy,
  Bug,
  Lightbulb,
  HelpCircle,
  MessageCircle,
  AlertCircle,
  Clock,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getSupportRequests } from '@business-assistant/shared/services/supportService';
import type {
  SupportRequest,
  SupportRequestType,
  SupportPriority,
  SupportStatus,
} from '@business-assistant/shared/domain/support';

// =============================================================================
// Constants
// =============================================================================

const TYPE_CONFIG: Record<SupportRequestType, { icon: React.ElementType; label: string; color: string }> = {
  bug: { icon: Bug, label: 'Bug', color: '#ef4444' },
  feature: { icon: Lightbulb, label: 'Feature', color: '#f59e0b' },
  support: { icon: LifeBuoy, label: 'Supporto', color: '#3b82f6' },
  question: { icon: HelpCircle, label: 'Domanda', color: '#8b5cf6' },
};

const PRIORITY_COLORS: Record<SupportPriority, string> = {
  urgent: '#ef4444',
  high: '#f59e0b',
  medium: '#3b82f6',
  low: '#6b7280',
};

const PRIORITY_LABELS: Record<SupportPriority, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  medium: 'Media',
  low: 'Bassa',
};

const STATUS_CONFIG: Record<SupportStatus, { label: string; color: string }> = {
  open: { label: 'Aperto', color: '#f59e0b' },
  in_progress: { label: 'In corso', color: '#3b82f6' },
  resolved: { label: 'Risolto', color: '#10b981' },
  closed: { label: 'Chiuso', color: '#6b7280' },
};

// =============================================================================
// Component
// =============================================================================

interface SupportTicketsWidgetProps {
  maxItems?: number;
}

export function SupportTicketsWidget({ maxItems = 5 }: SupportTicketsWidgetProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'owner';

  const [tickets, setTickets] = useState<SupportRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'in_progress'>('all');
  const [priorityFilter, setPriorityFilter] = useState<SupportPriority | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<SupportRequestType | 'all'>('all');

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = isAdmin ? undefined : { userId: user?.id };
      const result = await getSupportRequests(filters, 100, 0);
      // Only show open and in_progress
      const activeTickets = result.items.filter(t => t.status === 'open' || t.status === 'in_progress');
      setTickets(activeTickets);
    } catch (err) {
      console.error('Failed to load support tickets:', err);
      setError('Errore caricamento');
    } finally {
      setLoading(false);
    }
  };

  // Count by type
  const typeCounts = useMemo(() => {
    const counts: Record<SupportRequestType, number> = { bug: 0, feature: 0, support: 0, question: 0 };
    tickets.forEach(t => {
      if (counts[t.type] !== undefined) counts[t.type]++;
    });
    return counts;
  }, [tickets]);

  // Apply filters
  const filteredTickets = useMemo(() => {
    return tickets.filter(ticket => {
      if (statusFilter !== 'all' && ticket.status !== statusFilter) return false;
      if (priorityFilter !== 'all' && ticket.priority !== priorityFilter) return false;
      if (typeFilter !== 'all' && ticket.type !== typeFilter) return false;
      return true;
    });
  }, [tickets, statusFilter, priorityFilter, typeFilter]);

  const displayTickets = filteredTickets.slice(0, maxItems);
  const hasMore = filteredTickets.length > maxItems;

  // Count by status
  const openCount = tickets.filter(t => t.status === 'open').length;
  const inProgressCount = tickets.filter(t => t.status === 'in_progress').length;

  if (loading) {
    return (
      <div className="dashboard-widget">
        <div className="widget-header">
          <LifeBuoy size={18} />
          <span>Supporto</span>
        </div>
        <div className="widget-content" style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
          <RefreshCw size={20} className="spinner" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-widget">
        <div className="widget-header">
          <LifeBuoy size={18} />
          <span>Supporto</span>
        </div>
        <div className="widget-content" style={{ color: 'var(--color-error)', padding: '12px', fontSize: '13px' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-widget support-widget">
      {/* Header */}
      <div className="widget-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <LifeBuoy size={18} />
          <span>Supporto</span>
          <span style={{
            fontSize: '11px',
            padding: '2px 6px',
            borderRadius: '8px',
            background: tickets.length > 0 ? 'var(--color-warning-light, rgba(245, 158, 11, 0.15))' : 'var(--color-bg-tertiary)',
            color: tickets.length > 0 ? 'var(--color-warning, #f59e0b)' : 'var(--color-text-secondary)',
          }}>
            {openCount} aperti • {inProgressCount} in corso
          </span>
        </div>
        <button
          onClick={loadTickets}
          style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', color: 'var(--color-text-secondary)' }}
          title="Aggiorna"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Type Boxes - Info + Filter */}
      <div style={{
        display: 'flex',
        gap: '6px',
        padding: '8px 12px',
        borderBottom: '1px solid var(--color-border)',
        flexWrap: 'wrap',
      }}>
        {(Object.keys(TYPE_CONFIG) as SupportRequestType[]).map(type => {
          const config = TYPE_CONFIG[type];
          const count = typeCounts[type];
          const isSelected = typeFilter === type;
          const Icon = config.icon;

          return (
            <button
              key={type}
              onClick={() => setTypeFilter(isSelected ? 'all' : type)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                borderRadius: '6px',
                border: isSelected ? `2px solid ${config.color}` : '1px solid var(--color-border)',
                background: isSelected ? `${config.color}15` : 'var(--color-bg)',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: isSelected ? 600 : 400,
                color: isSelected ? config.color : 'var(--color-text)',
                opacity: count === 0 ? 0.5 : 1,
                transition: 'all 0.15s',
              }}
            >
              <Icon size={12} />
              <span>{count}</span>
              <span style={{ color: isSelected ? config.color : 'var(--color-text-secondary)' }}>
                {config.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Compact Filters Row */}
      <div style={{
        display: 'flex',
        gap: '8px',
        padding: '6px 12px',
        borderBottom: '1px solid var(--color-border)',
        background: 'var(--color-bg-secondary)',
      }}>
        {/* Status */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          style={{
            padding: '3px 6px',
            fontSize: '11px',
            borderRadius: '4px',
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg)',
            cursor: 'pointer',
          }}
        >
          <option value="all">Tutti</option>
          <option value="open">Aperti</option>
          <option value="in_progress">In corso</option>
        </select>

        {/* Priority */}
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value as any)}
          style={{
            padding: '3px 6px',
            fontSize: '11px',
            borderRadius: '4px',
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg)',
            cursor: 'pointer',
          }}
        >
          <option value="all">Tutte priorità</option>
          <option value="urgent">Urgente</option>
          <option value="high">Alta</option>
          <option value="medium">Media</option>
          <option value="low">Bassa</option>
        </select>

        {/* Reset */}
        {(typeFilter !== 'all' || statusFilter !== 'all' || priorityFilter !== 'all') && (
          <button
            onClick={() => { setTypeFilter('all'); setStatusFilter('all'); setPriorityFilter('all'); }}
            style={{
              padding: '3px 8px',
              fontSize: '11px',
              borderRadius: '4px',
              border: 'none',
              background: 'var(--color-text-tertiary)',
              color: 'white',
              cursor: 'pointer',
            }}
          >
            Reset
          </button>
        )}
      </div>

      {/* Content */}
      <div className="widget-content" style={{ maxHeight: '300px', overflowY: 'auto' }}>
        {filteredTickets.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '20px',
            color: 'var(--color-text-secondary)',
            fontSize: '13px',
          }}>
            <LifeBuoy size={28} style={{ opacity: 0.3, marginBottom: '8px' }} />
            <p>{typeFilter !== 'all' || statusFilter !== 'all' || priorityFilter !== 'all'
              ? 'Nessun ticket con questi filtri'
              : 'Nessun ticket attivo'}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {displayTickets.map(ticket => {
              const typeConfig = TYPE_CONFIG[ticket.type];
              const TypeIcon = typeConfig.icon;
              const statusConfig = STATUS_CONFIG[ticket.status];
              const priorityColor = PRIORITY_COLORS[ticket.priority];

              return (
                <div
                  key={ticket.id}
                  onClick={() => navigate('/settings/support')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid var(--color-border)',
                    transition: 'background 0.15s',
                  }}
                  className="ticket-row"
                >
                  {/* Type Icon + Priority dot */}
                  <div style={{
                    position: 'relative',
                    padding: '6px',
                    borderRadius: '6px',
                    background: `${typeConfig.color}15`,
                  }}>
                    <TypeIcon size={14} style={{ color: typeConfig.color }} />
                    <div style={{
                      position: 'absolute',
                      top: '-2px',
                      right: '-2px',
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: priorityColor,
                      border: '2px solid var(--color-bg)',
                    }} />
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: 500,
                      fontSize: '13px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {ticket.subject}
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '11px',
                      color: 'var(--color-text-secondary)',
                      marginTop: '2px',
                    }}>
                      <span style={{ color: statusConfig.color, display: 'flex', alignItems: 'center', gap: '2px' }}>
                        {ticket.status === 'open' ? <AlertCircle size={10} /> : <Clock size={10} />}
                        {statusConfig.label}
                      </span>
                      <span>•</span>
                      <span>{ticket.userName}</span>
                    </div>
                  </div>

                  <ChevronRight size={14} style={{ color: 'var(--color-text-tertiary)' }} />
                </div>
              );
            })}

            {hasMore && (
              <button
                onClick={() => navigate('/settings/support')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  padding: '8px',
                  color: 'var(--color-primary)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  width: '100%',
                }}
              >
                Vedi tutti ({filteredTickets.length})
                <ChevronRight size={12} />
              </button>
            )}
          </div>
        )}
      </div>

      <style>{`
        .support-widget .ticket-row:hover {
          background: var(--color-bg-hover);
        }
      `}</style>
    </div>
  );
}

export default SupportTicketsWidget;
